import { Button, Card, Col, Divider, Empty, List, Row, Tooltip, Typography } from "antd";
import axios from "axios";
import CreateOrEditContact from "components/EditCompanyContacts/CompanyContactBox/CreateOrEditContact";
import { SpinLoad } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import { CompanyContact } from "@bindeleddet/core/types";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";

interface Props {
  companyId: number;
}

const CustomCheckbox: React.FC<{ checked: boolean; onChange: () => void; disabled: boolean }> = ({
  checked,
  onChange,
  disabled,
}) => {
  return (
    <Tooltip title={checked ? "Hovedkontakt" : "Sett som hovedkontakt"}>
      <div
        onClick={!disabled ? onChange : undefined}
        onKeyDown={
          !disabled
            ? e => {
                if (e.key === "Enter") onChange();
              }
            : undefined
        }
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        style={{
          display: "inline-block",
          width: "20px",
          height: "20px",
          backgroundColor: checked ? "var(--primary-color)" : "#f0f0f0",
          textAlign: "center",
          lineHeight: "20px",
          borderRadius: "20px",
          cursor: disabled ? "not-allowed" : "pointer",
          fontWeight: "bold",
          color: checked ? "white" : "black",
        }}
      >
        {checked ? "H" : "V"}
      </div>
    </Tooltip>
  );
};

const AdminCompanyContacts: React.FC<Props> = ({ companyId }) => {
  const [contacts, setContacts] = useState<CompanyContact[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [mainLoading, setMainLoading] = useState<boolean>(false);
  const [showCreateContactModal, setShowCreateContactModal] = useState<boolean>(false);
  const [editingContactId, setEditingContactId] = useState<number | null>(null);

  useEffect(() => {
    axios
      .get<CompanyContact[]>(`/companies/${companyId}/contact-persons/`)
      .then(response => {
        setContacts(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [companyId]);

  const handleMain = async (changingContact: CompanyContact) => {
    setMainLoading(true);
    if (changingContact.main_contact && contacts.filter(contact => contact.main_contact).length === 1) {
      setMainLoading(false);
      return message("error", "Feil under oppdatering", "Bedriften må ha en hovedkontakt.");
    }
    try {
      const putResponse = await axios.put<CompanyContact[]>(
        `/companies/${companyId}/contact-persons/${changingContact.id}/`,
        { ...changingContact, main_contact: !changingContact.main_contact }
      );
      if (putResponse.status === 200) {
        const mainContact = contacts.find(contact => contact.main_contact && contact.id !== changingContact.id);
        if (mainContact) {
          await axios.put(`/companies/${companyId}/contact-persons/${mainContact.id}/`, {
            ...mainContact,
            main_contact: false,
          });
        }
        message("success", "Hovedkontakt oppdatert");
        setContacts(prevContacts => {
          return prevContacts.map(contact => {
            if (contact.id === changingContact.id) {
              return {
                ...contact,
                main_contact: !changingContact.main_contact,
              };
            }
            if (mainContact && contact.id === mainContact.id) {
              return {
                ...contact,
                main_contact: false,
              };
            }
            return contact;
          });
        });
        setMainLoading(false);
      } else {
        message("error", "Kunne ikke oppdatere hovedkontakt");
        setMainLoading(false);
      }
    } catch (error) {
      message("error", "Kunne ikke oppdatere hovedkontakt");
      setMainLoading(false);
    }
  };

  /*   const handleDelete = (contact: CompanyContact) => {
      axios
        .delete<CompanyContact>(`/companies/${contact.company}/contact-persons/${contact.id}/`)
        .then(() => {
          message("success", "Kontaktperson slettet");
          setContacts(prevContacts => prevContacts.filter(prevContact => prevContact.id !== contact.id));
        })
        .catch(() => message("error", "Kontaktperson ikke slettet"));
    }; */

  const handleCreate = (contact: CompanyContact) => setContacts(prevContacts => [...prevContacts, contact]);
  const handleUpdate = (contact: CompanyContact) => {
    setContacts(prevContacts =>
      prevContacts.map(prevContact => (prevContact.id === contact.id ? contact : prevContact))
    );
  };
  const contactBox = contacts.map(contact => {
    return (
      <div key={contact.id}>
        <Row gutter={5} style={{ fontSize: "12px" }}>
          <Col span={2}>
            <Tooltip title={contact.main_contact ? "Hovedkontakt" : "Vanlig bedriftskontakt"}>
              <CustomCheckbox
                checked={contact.main_contact}
                onChange={() => handleMain(contact)}
                disabled={mainLoading}
              />
            </Tooltip>
          </Col>
          <Col span={10}>
            <CreateOrEditContact
              key={contact.id}
              companyId={companyId}
              contact={contact}
              handleUpdate={updatedContact => handleUpdate(updatedContact)}
              isOpen={editingContactId === contact.id}
              onClose={() => setEditingContactId(null)}
            />
            <Typography.Text
              style={{ textDecoration: "underline", cursor: "pointer" }}
              onClick={() => setEditingContactId(contact.id)}
            >
              {contact.first_name} {contact.last_name}
            </Typography.Text>
          </Col>
          <Col span={6}>{contact.position}</Col>
          <Col span={6}>{dayjs(contact.updated_at).format("DD.MM.YYYY")}</Col>
        </Row>
        <Divider style={{ margin: "12px 0" }} />
      </div>
    );
  });

  return (
    <>
      <Card title="Kontaktpersoner" style={{ marginTop: 16, width: "100%" }}>
        <Row gutter={5} style={{ fontSize: "12px" }}>
          <Col span={2} />
          <Col span={10} style={{ fontWeight: "bold" }}>
            Navn
          </Col>
          <Col span={6} style={{ fontWeight: "bold" }}>
            Stilling
          </Col>
          <Col span={6} style={{ fontWeight: "bold" }}>
            Oppdatert
          </Col>
        </Row>
        <Divider style={{ margin: "6px 0 12px 0" }} />
        <List
          itemLayout="vertical"
          pagination={false}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Fant ingen kontaktpersoner" />,
          }}
          style={{ padding: 0, margin: 0, marginTop: 12 }}
          dataSource={contactBox}
          renderItem={item => item}
          loading={{ indicator: <SpinLoad />, spinning: loading }}
        />
        <CreateOrEditContact
          companyId={companyId}
          handleCreate={handleCreate}
          isOpen={showCreateContactModal}
          onClose={() => setShowCreateContactModal(false)}
        />
        <Button style={{ width: "100%" }} type="primary" onClick={() => setShowCreateContactModal(true)}>
          Ny kontaktperson
        </Button>
      </Card>
    </>
  );
};

export default AdminCompanyContacts;
